import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, ArrowLeft, UserPlus, CheckCircle2, X,
    Mail, Phone, MessageSquare, Calendar, Link2, Copy, Send, Sparkles, Users2, Download,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AthleteNameLink from "@/components/coach/AthleteNameLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Mnp() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [invites, setInvites] = useState([]);
    const [busy, setBusy] = useState(true);
    const [acting, setActing] = useState(null);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [tab, setTab] = useState("queue");

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner") {
            toast.error("Owner-only");
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const [q, i] = await Promise.all([
                axios.get(`${API}/coach/mnp`, { withCredentials: true }),
                axios.get(`${API}/coach/mnp/invites`, { withCredentials: true }),
            ]);
            setRows(q.data.signups || []);
            setInvites(i.data.invites || []);
        } catch (e) {
            toast.error("Couldn't load MNP data", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { if (user?.role === "owner") load(); }, [user]);

    const approve = async (uid) => {
        setActing(uid);
        try {
            const r = await axios.post(`${API}/coach/mnp/${uid}/approve`, {}, { withCredentials: true });
            toast.success(
                r.data?.email_sent
                    ? "Approved — sign-in link emailed"
                    : "Approved — but email failed to send (check Resend logs)"
            );
            load();
        } catch (e) {
            toast.error("Approval failed", { description: e?.response?.data?.detail });
        } finally {
            setActing(null);
        }
    };

    const deny = async (uid, name) => {
        const reason = window.prompt(`Deny ${name}'s signup? Optional reason:`, "");
        if (reason === null) return;
        setActing(uid);
        try {
            await axios.post(`${API}/coach/mnp/${uid}/deny`, { reason }, { withCredentials: true });
            toast.success("Signup removed");
            load();
        } catch (e) {
            toast.error("Couldn't deny", { description: e?.response?.data?.detail });
        } finally {
            setActing(null);
        }
    };

    if (loading || !user || busy) {
        return (
            <div className="min-h-screen bg-black text-white flex items-center justify-center" data-testid="mnp-loading">
                <Loader2 className="w-7 h-7 text-emerald-400 animate-spin" />
            </div>
        );
    }

    return (
        <div data-testid="coach-mnp" className="min-h-screen bg-black text-white">
            <div className="max-w-5xl mx-auto px-6 py-10">
                <Link to="/coach" className="text-zinc-400 hover:text-white text-sm flex items-center gap-2 mb-6" data-testid="mnp-back">
                    <ArrowLeft className="w-4 h-4" /> Back to coach
                </Link>
                <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
                    <div className="flex items-center gap-3">
                        <UserPlus className="w-7 h-7 text-emerald-400" />
                        <h1 className="text-3xl font-bold">Members Needing Profile</h1>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => setBulkOpen(true)}
                            className="flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded px-4 py-2 text-sm font-medium"
                            data-testid="mnp-bulk-invite"
                        >
                            <Users2 className="w-4 h-4" /> Bulk invite
                        </button>
                        <button
                            type="button"
                            onClick={() => setInviteOpen(true)}
                            className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-600 rounded px-4 py-2 text-sm font-medium"
                            data-testid="mnp-generate-invite"
                        >
                            <Link2 className="w-4 h-4" /> Generate invite link
                        </button>
                    </div>
                </div>
                <p className="text-zinc-400 text-sm mb-6 max-w-2xl">
                    Send signup invite links directly to parents (text/iMessage/email). Invited members skip the queue and get signed in automatically.
                </p>

                <div className="flex gap-1 mb-6 border-b border-zinc-800" data-testid="mnp-tabs">
                    <TabBtn active={tab === "queue"} onClick={() => setTab("queue")} testid="mnp-tab-queue">
                        Public queue {rows.length > 0 && <span className="ml-1 text-xs bg-red-600 text-white px-1.5 py-0.5 rounded">{rows.length}</span>}
                    </TabBtn>
                    <TabBtn active={tab === "invites"} onClick={() => setTab("invites")} testid="mnp-tab-invites">
                        Invite links <span className="ml-1 text-xs text-zinc-500">({invites.length})</span>
                    </TabBtn>
                </div>

                {tab === "queue" ? (
                    rows.length === 0 ? (
                        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-12 text-center" data-testid="mnp-empty">
                            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3 opacity-60" />
                            <p className="text-zinc-400">No pending signups in the public queue.</p>
                            <p className="text-zinc-500 text-sm mt-2">For most members, just <button type="button" onClick={() => setInviteOpen(true)} className="text-emerald-400 hover:underline">generate an invite link</button> instead — it's faster.</p>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="mnp-list">
                            {rows.map((r) => (
                                <SignupCard
                                    key={r.user_id}
                                    row={r}
                                    approving={acting === r.user_id}
                                    onApprove={() => approve(r.user_id)}
                                    onDeny={() => deny(r.user_id, r.name)}
                                />
                            ))}
                        </div>
                    )
                ) : (
                    <InvitesPanel
                        invites={invites}
                        onRevoke={async (tok) => {
                            if (!window.confirm("Revoke this unused invite? The link will stop working.")) return;
                            try {
                                await axios.delete(`${API}/coach/mnp/invites/${tok}`, { withCredentials: true });
                                toast.success("Invite revoked");
                                load();
                            } catch (e) {
                                toast.error("Revoke failed", { description: e?.response?.data?.detail });
                            }
                        }}
                    />
                )}
            </div>
            {inviteOpen && (
                <GenerateInviteModal
                    onClose={() => setInviteOpen(false)}
                    onCreated={() => { setInviteOpen(false); setTab("invites"); load(); }}
                />
            )}
            {bulkOpen && (
                <BulkInviteModal
                    onClose={() => setBulkOpen(false)}
                    onCreated={() => { setBulkOpen(false); setTab("invites"); load(); }}
                />
            )}
        </div>
    );
}

function TabBtn({ active, onClick, testid, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${active ? "border-emerald-500 text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
            data-testid={testid}
        >
            {children}
        </button>
    );
}

function InvitesPanel({ invites, onRevoke }) {
    const [resending, setResending] = useState(null);
    const resend = async (token) => {
        setResending(token);
        try {
            const r = await axios.post(`${API}/coach/mnp/invites/${token}/resend`, {}, { withCredentials: true });
            toast.success("Invite email re-sent");
        } catch (e) {
            toast.error("Resend failed", { description: e?.response?.data?.detail });
        } finally {
            setResending(null);
        }
    };
    if (invites.length === 0) {
        return (
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-12 text-center" data-testid="invites-empty">
                <Sparkles className="w-12 h-12 text-emerald-400 mx-auto mb-3 opacity-60" />
                <p className="text-zinc-400">No invites generated yet.</p>
                <p className="text-zinc-500 text-sm mt-1">Click <b>Generate invite link</b> above to send your first one.</p>
            </div>
        );
    }
    return (
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden" data-testid="invites-list">
            <table className="w-full text-sm">
                <thead className="bg-zinc-900 text-zinc-400">
                    <tr>
                        <th className="text-left px-5 py-2.5">For</th>
                        <th className="text-left px-5 py-2.5">Status</th>
                        <th className="text-left px-5 py-2.5">Created</th>
                        <th className="text-left px-5 py-2.5">Used by</th>
                        <th className="text-right px-5 py-2.5">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {invites.map((inv) => (
                        <tr key={inv.token} className="border-t border-zinc-900" data-testid={`invite-row-${inv.token}`}>
                            <td className="px-5 py-2.5">
                                <div className="text-white">{inv.name || <span className="text-zinc-500">(no name set)</span>}</div>
                                <div className="text-xs text-zinc-500">{inv.email || "—"}</div>
                                {inv.note && <div className="text-xs text-zinc-400 italic mt-0.5">"{inv.note}"</div>}
                            </td>
                            <td className="px-5 py-2.5">
                                {inv.used ? (
                                    <span className="px-2 py-0.5 rounded text-xs bg-emerald-900/40 text-emerald-300">Used</span>
                                ) : inv.email_sent_at ? (
                                    <div>
                                        <span className="px-2 py-0.5 rounded text-xs bg-blue-900/40 text-blue-200">Emailed</span>
                                        <div className="text-[10px] text-zinc-500 mt-0.5">
                                            {inv.email_send_count > 1 ? `Re-sent ${inv.email_send_count}× · ` : ""}
                                            {new Date(inv.email_sent_at).toLocaleString()}
                                        </div>
                                    </div>
                                ) : (
                                    <span className="px-2 py-0.5 rounded text-xs bg-amber-900/40 text-amber-300">Pending</span>
                                )}
                                {inv.email_last_error && (
                                    <div className="text-[10px] text-red-300 mt-1 italic">Send error: {inv.email_last_error}</div>
                                )}
                            </td>
                            <td className="px-5 py-2.5 text-zinc-400 text-xs">
                                {inv.created_at ? new Date(inv.created_at).toLocaleString() : "—"}
                            </td>
                            <td className="px-5 py-2.5">
                                {inv.used ? (
                                    <div>
                                        <div className="text-white text-xs">{inv.used_by_name || inv.used_by_email}</div>
                                        <div className="text-xs text-zinc-500">{inv.used_at ? new Date(inv.used_at).toLocaleString() : ""}</div>
                                    </div>
                                ) : <span className="text-zinc-600 text-xs">—</span>}
                            </td>
                            <td className="px-5 py-2.5 text-right">
                                {inv.used ? null : (
                                    <div className="flex justify-end gap-2 flex-wrap">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(inv.url);
                                                toast.success("Link copied");
                                            }}
                                            className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-emerald-300 flex items-center gap-1"
                                            data-testid={`invite-copy-${inv.token}`}
                                        >
                                            <Copy className="w-3 h-3" /> Copy
                                        </button>
                                        {inv.email && (
                                            <button
                                                type="button"
                                                onClick={() => resend(inv.token)}
                                                disabled={resending === inv.token}
                                                className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-blue-200 flex items-center gap-1 disabled:opacity-50"
                                                data-testid={`invite-resend-${inv.token}`}
                                            >
                                                {resending === inv.token ? "…" : "Resend email"}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => onRevoke(inv.token)}
                                            className="text-xs px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-red-300"
                                            data-testid={`invite-revoke-${inv.token}`}
                                        >Revoke</button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function GenerateInviteModal({ onClose, onCreated }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [generated, setGenerated] = useState(null);

    const submit = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${API}/coach/mnp/invites`, {
                name: name.trim() || null,
                email: email.trim() || null,
                note: note.trim() || null,
            }, { withCredentials: true });
            setGenerated(r.data);
            if (r.data?.email_sent) {
                toast.success(`Invite emailed to ${r.data?.invite?.email}`, {
                    description: "They'll see a MaxSC-themed message with the signup link.",
                });
            } else if (r.data?.invite?.email) {
                toast.warning("Invite created but email didn't send", {
                    description: r.data?.email_error || "Use the copy button to text it instead.",
                });
            } else {
                toast.success("Invite link ready — copy it to share.");
            }
        } catch (e) {
            toast.error("Couldn't create invite", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const copy = () => {
        if (!generated?.url) return;
        navigator.clipboard.writeText(generated.url);
        toast.success("Link copied");
    };

    const smsText = generated && (() => {
        const firstName = (generated.invite?.name || "").split(" ")[0];
        return `Hi${firstName ? " " + firstName : ""}! I'd love to get you set up on our new training platform. Use this link to finish creating your account — it'll only take a minute: ${generated.url}`;
    })();

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="generate-invite-modal">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold">Generate signup invite</h3>
                        <p className="text-xs text-zinc-500 mt-0.5">Single-use link. Invited member is auto-approved and gets a sign-in email instantly.</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="invite-modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {!generated ? (
                    <>
                        <div className="space-y-3 text-sm">
                            <Field label="Name (optional)" hint="Pre-fills the signup form so they don't have to retype it.">
                                <input
                                    data-testid="invite-name"
                                    type="text" value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Jane Smith"
                                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                                />
                            </Field>
                            <Field label="Email (optional)" hint="Pre-fills the signup form. They can still change it.">
                                <input
                                    data-testid="invite-email"
                                    type="email" value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="jane@example.com"
                                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                                />
                            </Field>
                            <Field label="Internal note (optional)" hint="Just for you — what this invite is for.">
                                <input
                                    data-testid="invite-note"
                                    type="text" value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Tommy's mom — Thurs class"
                                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2"
                                />
                            </Field>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-zinc-400 hover:text-white">Cancel</button>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={busy}
                                className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded flex items-center gap-2"
                                data-testid="invite-create"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                                Generate link
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="bg-emerald-950/40 border border-emerald-800 rounded-lg p-4 mb-3" data-testid="invite-generated">
                            <p className="text-xs uppercase tracking-wide text-emerald-400 mb-1">Invite link</p>
                            <p className="text-sm font-mono break-all text-white">{generated.url}</p>
                        </div>
                        <button
                            type="button"
                            onClick={copy}
                            className="w-full px-3 py-2.5 text-sm bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded flex items-center justify-center gap-2 mb-2"
                            data-testid="invite-copy-link"
                        >
                            <Copy className="w-4 h-4" /> Copy link
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                navigator.clipboard.writeText(smsText);
                                toast.success("Pre-filled text copied — paste into iMessage");
                            }}
                            className="w-full px-3 py-2.5 text-sm bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded flex items-center justify-center gap-2 mb-4"
                            data-testid="invite-copy-text"
                        >
                            <Send className="w-4 h-4" /> Copy as text message
                        </button>
                        <p className="text-xs text-zinc-500 mb-4">The text version includes a friendly message + the link, ready to paste into iMessage or SMS.</p>
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={onCreated}
                                className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 rounded"
                                data-testid="invite-done"
                            >Done</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function BulkInviteModal({ onClose, onCreated }) {
    const [raw, setRaw] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    // Parse pasted text: each line is "email" or "email, name" or "name, email"
    // (we detect which side looks like an email).
    const parsed = (() => {
        const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        return lines.map((line) => {
            const parts = line.split(/[,\t]/).map((p) => p.trim()).filter(Boolean);
            if (parts.length === 0) return null;
            let email = null, name = null;
            for (const p of parts) {
                if (!email && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(p)) email = p;
                else if (!name) name = p;
            }
            if (!email && !name) return null;
            return { email, name };
        }).filter(Boolean);
    })();

    const submit = async () => {
        if (parsed.length === 0) {
            toast.error("Paste at least one email or name per line");
            return;
        }
        setBusy(true);
        try {
            const r = await axios.post(`${API}/coach/mnp/invites/bulk`, {
                rows: parsed,
                note: "Bulk migration",
            }, { withCredentials: true });
            setResult(r.data);
            toast.success(`Generated ${r.data?.summary?.created || 0} invite${r.data?.summary?.created === 1 ? "" : "s"}`);
        } catch (e) {
            toast.error("Bulk invite failed", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    const downloadCsv = () => {
        if (!result?.created?.length) return;
        const rows = [
            ["Name", "Email", "Invite link", "Ready-to-send text"],
            ...result.created.map((c) => [c.name || "", c.email || "", c.url, c.sms_text]),
        ];
        const csv = rows.map((row) =>
            row.map((cell) => {
                const s = String(cell ?? "");
                return s.includes(",") || s.includes('"') || s.includes("\n")
                    ? `"${s.replace(/"/g, '""')}"`
                    : s;
            }).join(",")
        ).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `maxsc_invites_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="bulk-invite-modal">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold">Bulk invite</h3>
                        <p className="text-xs text-zinc-500 mt-0.5">Migrating from your old CRM? Paste a list and get a CSV of personal invite links.</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="bulk-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {!result ? (
                    <>
                        <label className="block text-xs text-zinc-400 mb-1">Paste one per line</label>
                        <p className="text-xs text-zinc-500 mb-2">
                            Formats accepted: <code>email@x.com</code> or <code>email@x.com, Jane Smith</code> or <code>Jane Smith, email@x.com</code>
                        </p>
                        <textarea
                            data-testid="bulk-input"
                            rows={10}
                            value={raw}
                            onChange={(e) => setRaw(e.target.value)}
                            placeholder={"jane@example.com, Jane Smith\ntom.parent@example.com\nlee.fam@example.com, The Lee Family"}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm font-mono"
                        />
                        <p className="text-xs text-zinc-500 mt-2" data-testid="bulk-preview-count">
                            Detected: <span className="text-white font-semibold">{parsed.length}</span> row{parsed.length === 1 ? "" : "s"}
                        </p>
                        <div className="flex justify-end gap-2 mt-5">
                            <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-zinc-400 hover:text-white">Cancel</button>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={busy || parsed.length === 0}
                                className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded flex items-center gap-2"
                                data-testid="bulk-submit"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                                Generate {parsed.length} link{parsed.length === 1 ? "" : "s"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="bg-emerald-950/40 border border-emerald-800 rounded-lg p-4 mb-4" data-testid="bulk-result">
                            <p className="text-sm text-emerald-300 font-semibold">
                                ✓ Generated {result.summary.created} invite{result.summary.created === 1 ? "" : "s"}
                                {result.summary.skipped > 0 && <span className="text-amber-300"> · {result.summary.skipped} skipped</span>}
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={downloadCsv}
                            className="w-full px-4 py-3 text-sm bg-emerald-700 hover:bg-emerald-600 rounded flex items-center justify-center gap-2 mb-4 font-medium"
                            data-testid="bulk-download-csv"
                        >
                            <Download className="w-4 h-4" /> Download CSV
                        </button>

                        <p className="text-xs text-zinc-500 mb-2">Preview (first 10):</p>
                        <div className="bg-zinc-900 rounded border border-zinc-800 overflow-hidden max-h-72 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-zinc-900 text-zinc-400 sticky top-0">
                                    <tr>
                                        <th className="text-left px-3 py-2">Name / Email</th>
                                        <th className="text-right px-3 py-2">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.created.slice(0, 10).map((c) => (
                                        <tr key={c.token} className="border-t border-zinc-800">
                                            <td className="px-3 py-2">
                                                <div className="text-white">{c.name || c.email || "(blank)"}</div>
                                                {c.name && c.email && <div className="text-zinc-500">{c.email}</div>}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(c.url);
                                                        toast.success("Link copied");
                                                    }}
                                                    className="text-emerald-400 hover:text-emerald-300"
                                                >Copy</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {result.created.length > 10 && (
                            <p className="text-xs text-zinc-500 mt-2 text-center">+ {result.created.length - 10} more — download the CSV to see all</p>
                        )}

                        <div className="flex justify-end mt-5">
                            <button
                                type="button"
                                onClick={onCreated}
                                className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 rounded"
                                data-testid="bulk-done"
                            >Done</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-xs text-zinc-400 mb-1">{label}</label>
            {hint && <p className="text-xs text-zinc-600 mb-1">{hint}</p>}
            {children}
        </div>
    );
}

function SignupCard({ row, approving, onApprove, onDeny }) {
    const sentAt = row.mnp_signup_at ? new Date(row.mnp_signup_at).toLocaleString() : "—";
    return (
        <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-5" data-testid={`mnp-card-${row.user_id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                    <h3 className="text-lg font-semibold">{row.name}</h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-400 mt-1">
                        <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {row.email}</span>
                        {row.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {row.phone}</span>}
                        <span className="flex items-center gap-1 text-zinc-500"><Calendar className="w-3.5 h-3.5" /> {sentAt}</span>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onDeny}
                        disabled={approving}
                        className="px-3 py-2 text-sm text-zinc-400 hover:text-red-400 disabled:opacity-50"
                        data-testid={`mnp-deny-${row.user_id}`}
                    >
                        <X className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={onApprove}
                        disabled={approving}
                        className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded flex items-center gap-2"
                        data-testid={`mnp-approve-${row.user_id}`}
                    >
                        {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Approve
                    </button>
                </div>
            </div>

            {row.mnp_how_we_know_you && (
                <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3 mb-3 flex gap-2" data-testid={`mnp-note-${row.user_id}`}>
                    <MessageSquare className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-zinc-300 whitespace-pre-wrap">{row.mnp_how_we_know_you}</p>
                </div>
            )}

            {row.athletes && row.athletes.length > 0 && (
                <div data-testid={`mnp-athletes-${row.user_id}`}>
                    <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Athletes ({row.athletes.length})</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                        {row.athletes.map((a) => (
                            <div key={a.athlete_id} className="bg-zinc-900/60 border border-zinc-800 rounded px-3 py-2 text-sm">
                                <AthleteNameLink
                                    athleteId={a.athlete_id}
                                    name={`${a.first_name || ""} ${a.last_name || ""}`.trim()}
                                    testid={`mnp-athlete-${a.athlete_id}`}
                                    className="font-medium text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                                />
                                {a.grade && <span className="text-zinc-500 ml-2 text-xs">· {a.grade}</span>}
                                {a.sports && a.sports.length > 0 && (
                                    <div className="text-xs text-zinc-500 mt-0.5">{a.sports.join(", ")}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
